import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, ArrowRight, Loader2 } from "lucide-react";
import { API } from "../lib/api";
import { useToolkitRatings } from "./RatingBadge";
import { Star } from "lucide-react";

export const addToBundle = (id) => {
  window.dispatchEvent(new CustomEvent("wwr:add-to-bundle", { detail: id }));
  document.getElementById("shop")?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const fmt = (n) => `$${Number(n).toFixed(2)}`;

const ToolkitOption = ({ t, on, toggle, r }) => (
  <button
    type="button"
    onClick={toggle}
    data-testid={`bundle-option-${t.id}`}
    aria-pressed={on}
    className={`w-full text-left rounded-xl border px-4 py-3 flex items-center gap-3 transition-colors ${
      on ? "border-teal-400/70 bg-teal-500/10" : "border-white/10 hover:border-white/30 bg-black/20"
    }`}
  >
    <span className={`w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 ${on ? "bg-teal-400 border-teal-400 text-black" : "border-white/30"}`}>
      {on && <Check size={13} />}
    </span>
    <span className="flex-1 min-w-0">
      <span className="block text-sm text-neutral-100 truncate">{t.title}</span>
      <span className="block text-[11px] text-neutral-500">
        {t.category}{t.level ? ` · ${t.level}` : ""}
        {r && <span className="ml-2 text-amber-300/90" data-testid={`bundle-rating-${t.id}`}><Star size={10} className="inline -mt-0.5 fill-amber-300" /> {r.avg.toFixed(1)} ({r.count})</span>}
      </span>
    </span>
    <span className="text-sm text-neutral-300">{fmt(t.price)}</span>
  </button>
);

export const BundleBuilder = () => {
  const [cat, setCat] = useState(null);
  const [picked, setPicked] = useState([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const ratings = useToolkitRatings();

  useEffect(() => {
    axios.get(`${API}/shop/catalog`).then(({ data }) => setCat(data));
    const onAdd = (e) => setPicked((p) => (p.includes(e.detail) ? p : [...p, e.detail]));
    window.addEventListener("wwr:add-to-bundle", onAdd);
    return () => window.removeEventListener("wwr:add-to-bundle", onAdd);
  }, []);

  if (!cat) return null;
  const n = picked.length;
  const full = cat.toolkits.filter((t) => picked.includes(t.id)).reduce((s, t) => s + t.price, 0);
  const total = n === 0 ? 0 : n === 1 ? full : Math.min(full, cat.tiers[n]);
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const checkout = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/shop/checkout`, {
        items: picked, email: email.trim(), origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail?.toString() || "Could not start checkout");
      setLoading(false);
    }
  };

  return (
    <div className="card-surface p-6 md:p-8" data-testid="bundle-builder">
      <div className="text-xs uppercase tracking-[0.28em] text-teal-300/80 mb-2">Build your bundle</div>
      <h3 className="font-display text-2xl md:text-3xl text-neutral-100 mb-2">Mix and match. Save more.</h3>
      <p className="text-sm text-neutral-400 mb-6" data-testid="bundle-tiers">
        Any 2 for {fmt(cat.tiers[2])} · any 3 for {fmt(cat.tiers[3])} · any 4 for {fmt(cat.tiers[4])} · all 5 for {fmt(cat.tiers[5])}
      </p>
      <div className="space-y-2 mb-6">
        {cat.toolkits.map((t) => (
          <ToolkitOption key={t.id} t={t} r={ratings[t.id]} on={picked.includes(t.id)} toggle={() => toggle(t.id)} />
        ))}
      </div>
      <div className="flex items-baseline justify-between mb-5" data-testid="bundle-summary">
        <span className="text-sm text-neutral-400">{n} selected</span>
        <span className="flex items-baseline gap-2">
          {n > 1 && full > total && <span className="text-sm text-neutral-500 line-through" data-testid="bundle-full-price">{fmt(full)}</span>}
          <span className="font-display text-3xl text-teal-200" data-testid="bundle-total">{fmt(total)}</span>
        </span>
      </div>
      <form onSubmit={checkout} className="flex flex-col sm:flex-row gap-3">
        <input
          type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="Email for your download links" data-testid="bundle-email"
          className="wwr-input flex-1 rounded-xl px-4 py-3 text-sm"
        />
        <button
          type="submit" disabled={n === 0 || loading} data-testid="bundle-checkout-btn"
          className="btn-primary px-6 py-3 inline-flex items-center justify-center gap-2 text-sm disabled:opacity-50"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <>Checkout <ArrowRight size={14} /></>}
        </button>
      </form>
    </div>
  );
};
